import { useState } from "react";
import { useParams } from "react-router-dom";
import { useMutation, useQuery } from "@tanstack/react-query";
import { ArrowRight, Check, Clock3, Copy, Download, LockKeyhole, RotateCcw, ShieldCheck, Sparkles, X } from "lucide-react";
import { api, money } from "../lib/api";
import { downloadReceiptPdf, readReceiptSession, receiptStorageKey, type ReceiptSession } from "../lib/receiptPdf";
import { ErrorState, LoadingState } from "../components/Ui";
import "./CheckoutPage.css";

type Checkout={merchant:{name:string;logoUrl?:string;supportEmail?:string};description:string;amountMinor:string;currency:string;customer?:{name:string;email:string};stripeConfigured:boolean;testMode?:boolean};
type BillingDetails={firstName:string;lastName:string;line1:string;city:string;country:string;region:string;postalCode:string;email:string;phone:string};
const emptyBilling:BillingDetails={firstName:"",lastName:"",line1:"",city:"",country:"US",region:"",postalCode:"",email:"",phone:""};
export function CheckoutPage() {
  const { token = "" } = useParams();
  const [step, setStep] = useState<1|2>(1);
  const [billing, setBilling] = useState<BillingDetails>(emptyBilling);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const details = useQuery({
    queryKey: ["checkout", token],
    queryFn: () => api<Checkout>(`/public/payment-links/${token}`),
  });
  const checkout = useMutation({
    mutationFn: () => api<{ checkoutUrl: string; testMode?: boolean; reference?: string }>(`/public/payment-links/${token}/checkout`, {
      method: "POST",
      body: JSON.stringify({ email: billing.email || undefined, idempotencyKey: crypto.randomUUID() }),
    }),
    onSuccess: (data) => {
      if (data.checkoutUrl) {
        const receiptSource=details.data;
        if(receiptSource){
          const receipt:ReceiptSession={
            merchantName:receiptSource.merchant.name,
            supportEmail:receiptSource.merchant.supportEmail,
            description:receiptSource.description,
            amountMinor:receiptSource.amountMinor,
            currency:receiptSource.currency,
            billing,
            reference:data.reference??`PP-${token.slice(-8).toUpperCase()}`,
            createdAt:new Date().toISOString(),
            testMode:Boolean(data.testMode??receiptSource.testMode),
          };
          window.sessionStorage.setItem(receiptStorageKey,JSON.stringify(receipt));
        }
        window.sessionStorage.setItem("paypilot:last-payment-url", window.location.pathname);
        window.location.assign(data.checkoutUrl);
      }
    },
  });

  if (details.isLoading) return <div className="public-state"><LoadingState label="Preparing your secure checkout"/></div>;
  if (details.error) return <div className="public-state"><ErrorState error={details.error}/></div>;

  const data = details.data!;
  const total = money(data.amountMinor, data.currency);
  const reference = token.slice(-8).toUpperCase();

  return (
    <main className="checkout-v2">
      <section className="checkout-v2__hero" aria-label="Payment summary">
        <div className="checkout-v2__brand">
          <span className="checkout-v2__brand-mark">
            {data.merchant.logoUrl ? <img src={data.merchant.logoUrl} alt=""/> : <Sparkles/>}
          </span>
          <strong>{data.merchant.name}</strong>
        </div>

        <div className="checkout-v2__summary">
          <div className="checkout-v2__secure-pill"><ShieldCheck/>Verified payment request</div>
          <p className="checkout-v2__kicker">Amount due</p>
          <h2 className="checkout-v2__amount">{total}</h2>
          <p className="checkout-v2__description">{data.description}</p>
          <div className="checkout-v2__meta">
            <div><span>Payment reference</span><strong>PP-{reference}</strong></div>
            <div><span>Currency</span><strong>{data.currency.toUpperCase()}</strong></div>
          </div>
        </div>

        <div className="checkout-v2__support">
          Questions about this payment?
          <strong>{data.merchant.supportEmail ?? "Contact the merchant directly"}</strong>
        </div>
      </section>

      <section className="checkout-v2__panel">
        <div className="checkout-v2__panel-inner">
          <div className="checkout-v2__steps" aria-label="Checkout progress">
            <div className={`checkout-v2__step ${step === 1 ? "is-active" : "is-complete"}`}><span className="checkout-v2__step-dot">1</span><span>Details</span></div>
            <div className={`checkout-v2__step ${step === 2 ? "is-active" : ""}`}><span className="checkout-v2__step-dot">2</span><span>Payment</span></div>
            <div className="checkout-v2__step"><span className="checkout-v2__step-dot">3</span><span>Receipt</span></div>
          </div>

          <div className={`checkout-v2__card ${step === 1 ? "checkout-v2__card--details" : ""}`} aria-live="polite">
            {step === 1 ? (
              <>
                <div className="checkout-v2__mode-row">
                  <span className="checkout-v2__kicker">Customer information</span>
                  {data.testMode && <span className="checkout-v2__test-badge">Test mode</span>}
                </div>
                <h1>Billing details</h1>
                <p className="checkout-v2__intro">Enter the billing information for this order, then continue to payment.</p>

                <form
                  className="checkout-v2__details-form"
                  onSubmit={(event) => {
                    event.preventDefault();
                    setStep(2);
                  }}
                >
                  <div className="checkout-v2__billing-grid">
                    <label className="checkout-v2__field">
                      <span>First name</span>
                      <input required autoComplete="given-name" value={billing.firstName} onChange={(event) => setBilling((current) => ({ ...current, firstName: event.target.value }))} />
                    </label>
                    <label className="checkout-v2__field">
                      <span>Last name</span>
                      <input required autoComplete="family-name" value={billing.lastName} onChange={(event) => setBilling((current) => ({ ...current, lastName: event.target.value }))} />
                    </label>
                    <label className="checkout-v2__field is-full">
                      <span>Billing address</span>
                      <input required autoComplete="street-address" placeholder="Street address" value={billing.line1} onChange={(event) => setBilling((current) => ({ ...current, line1: event.target.value }))} />
                    </label>
                    <label className="checkout-v2__field">
                      <span>City</span>
                      <input required autoComplete="address-level2" value={billing.city} onChange={(event) => setBilling((current) => ({ ...current, city: event.target.value }))} />
                    </label>
                    <label className="checkout-v2__field">
                      <span>State / Province</span>
                      <input required autoComplete="address-level1" value={billing.region} onChange={(event) => setBilling((current) => ({ ...current, region: event.target.value }))} />
                    </label>
                    <label className="checkout-v2__field">
                      <span>Country</span>
                      <select required autoComplete="country" value={billing.country} onChange={(event) => setBilling((current) => ({ ...current, country: event.target.value }))}>
                        <option value="US">United States</option>
                        <option value="GB">United Kingdom</option>
                        <option value="AE">United Arab Emirates</option>
                        <option value="CA">Canada</option>
                        <option value="AU">Australia</option>
                        <option value="PK">Pakistan</option>
                      </select>
                    </label>
                    <label className="checkout-v2__field">
                      <span>ZIP / Postal code</span>
                      <input required autoComplete="postal-code" value={billing.postalCode} onChange={(event) => setBilling((current) => ({ ...current, postalCode: event.target.value }))} />
                    </label>
                    <label className="checkout-v2__field">
                      <span>Email</span>
                      <input required type="email" autoComplete="email" value={billing.email} onChange={(event) => setBilling((current) => ({ ...current, email: event.target.value }))} />
                    </label>
                    <label className="checkout-v2__field">
                      <span>Phone</span>
                      <input required type="tel" autoComplete="tel" value={billing.phone} onChange={(event) => setBilling((current) => ({ ...current, phone: event.target.value }))} />
                    </label>
                  </div>
                  <button className="checkout-v2__button checkout-v2__details-action" type="submit">
                    Continue to payment <ArrowRight size={18} />
                  </button>
                </form>
              </>
            ) : (
              <>
            <div className="checkout-v2__mode-row">
              <p className="checkout-v2__eyebrow">{data.testMode ? "Checkout preview" : "Secure checkout"}</p>
              {data.testMode && <span className="checkout-v2__test-badge">Test mode</span>}
            </div>
            <h1>Complete your payment</h1>
            <p className="checkout-v2__intro">{data.testMode ? "Review the details below, then simulate a successful payment. No card details are requested and no funds will move." : "Review the details below. You’ll continue to Stripe to choose a supported payment method."}</p>

            <form onSubmit={(event) => { event.preventDefault(); if (termsAccepted) checkout.mutate(); }}>
              <div className="checkout-v2__billing-summary">
                <div><span>Billing to</span><strong>{billing.firstName} {billing.lastName}</strong></div>
                <div><span>Receipt</span><strong>{billing.email}</strong></div>
              </div>

              <div className="checkout-v2__order">
                <div className="checkout-v2__order-row"><span>Payment for</span><strong>{data.description}</strong></div>
                <div className="checkout-v2__order-row is-total"><span>Total due</span><strong>{total}</strong></div>
              </div>

              <label className="checkout-v2__terms">
                <input type="checkbox" checked={termsAccepted} onChange={(event) => setTermsAccepted(event.target.checked)} />
                <span>I confirm the billing details and accept the Terms &amp; Conditions and Privacy Policy.</span>
              </label>

              <div className="checkout-v2__payment-actions">
                <button className="checkout-v2__back" type="button" onClick={() => setStep(1)}>Back</button>
                <button className="checkout-v2__button" type="submit" disabled={!data.stripeConfigured || checkout.isPending || !termsAccepted}>
                {checkout.isPending ? "Recording test payment…" : data.testMode ? "Simulate successful payment" : data.stripeConfigured ? "Continue to secure payment" : "Payments not enabled"}
                <ArrowRight/>
                </button>
              </div>

              {data.testMode ? (
                <p className="checkout-v2__test-note"><strong>Testing only:</strong> this records a simulated sale for reporting and does not charge a payment method.</p>
              ) : !data.stripeConfigured && (
                <p className="checkout-v2__provider-note">The merchant is completing payment-provider setup. No payment can be submitted yet.</p>
              )}
              {checkout.error && <p className="checkout-v2__error" role="alert">{checkout.error.message}</p>}
            </form>

            <div className="checkout-v2__trust">
              <div className="checkout-v2__trust-item"><LockKeyhole/><span>Your connection is encrypted</span></div>
              <div className="checkout-v2__trust-item"><ShieldCheck/><span>{data.testMode ? "No card or bank details collected" : "Payment handled securely by Stripe"}</span></div>
            </div>
              </>
            )}
          </div>
          <p className="checkout-v2__powered">Powered by <strong>PayPilot</strong></p>
        </div>
      </section>
    </main>
  );
}

export function CheckoutResultPage({status}:{status:"success"|"processing"|"failed"|"canceled"}) {
  const [copied, setCopied] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const params = new URLSearchParams(window.location.search);
  const isTestMode = params.get("test_mode") === "1";
  const reference = params.get("reference");
  const sessionId = params.get("session_id");
  const receipt = readReceiptSession();
  const displayReference = reference ?? receipt?.reference ?? sessionId;
  const storedRetryUrl = window.sessionStorage.getItem("paypilot:last-payment-url");
  const retryUrl = storedRetryUrl?.startsWith("/pay/") ? storedRetryUrl : undefined;
  const retryable = status === "failed" || status === "canceled";
  const result = isTestMode
    ? { title: "Test payment recorded", message: "The simulated payment was completed successfully. No card was charged and no funds moved.", label: "PayPilot test mode" }
    : {
        success: { title: "Payment successful", message: "Your payment was received successfully. You may safely close this page.", label: "Payment complete" },
        processing: { title: "Payment is processing", message: "Your payment is still being confirmed. Please do not submit another payment while verification is in progress.", label: "Confirmation pending" },
        failed: { title: "Payment unsuccessful", message: "We could not complete this payment. No verified payment was recorded, and you can safely try again.", label: "Payment failed" },
        canceled: { title: "Payment canceled", message: "The checkout was canceled and you have not been charged. You can return to the payment request whenever you are ready.", label: "Checkout canceled" },
      }[status];
  const ResultIcon = status === "success" ? Check : status === "processing" ? Clock3 : X;

  const copyReference = async () => {
    if (!displayReference) return;
    await navigator.clipboard.writeText(displayReference);
    setCopied(true);
    window.setTimeout(() => setCopied(false), 1800);
  };

  const downloadReceipt = async () => {
    if (!receipt || !displayReference) return;
    setDownloading(true);
    try{
      await downloadReceiptPdf(receipt,displayReference,isTestMode?"Test paid":"Paid",sessionId);
    }finally{
      setDownloading(false);
    }
  };

  return (
    <main className={`payment-result payment-result--${status}`}>
      <div className="payment-result__orb payment-result__orb--one" />
      <div className="payment-result__orb payment-result__orb--two" />
      <section className="payment-result__shell">
        <div className="payment-result__brand"><span><Sparkles /></span><strong>PayPilot</strong></div>

        <div className="payment-result__progress" aria-label="Checkout completed">
          <div className="is-complete"><span><Check /></span><small>Details</small></div>
          <i />
          <div className="is-complete"><span><Check /></span><small>Payment</small></div>
          <i />
          <div className={status === "success" ? "is-complete" : "is-current"}><span><ResultIcon /></span><small>Receipt</small></div>
        </div>

        <div className="payment-result__card">
          <div className="payment-result__icon"><span><ResultIcon /></span></div>
          <span className="payment-result__eyebrow">{result.label}</span>
          <h1>{result.title}</h1>
          <p className="payment-result__message">{result.message}</p>

          {displayReference && (
            <div className="payment-result__reference">
              <div><span>Payment reference</span><strong>{displayReference}</strong></div>
              <button type="button" onClick={() => void copyReference()} aria-label="Copy payment reference">
                {copied ? <Check /> : <Copy />}{copied ? "Copied" : "Copy"}
              </button>
            </div>
          )}

          {status === "success" && receipt && displayReference && (
            <button className="payment-result__receipt-button" type="button" disabled={downloading} onClick={() => void downloadReceipt()}>
              <Download /> {downloading ? "Preparing receipt..." : "Download PDF receipt"}
            </button>
          )}

          {retryable && retryUrl ? (
            <a className="payment-result__action" href={retryUrl}><RotateCcw /> Try payment again</a>
          ) : retryable ? (
            <p className="payment-result__hint">Please reopen the original payment link or ask the seller to resend it.</p>
          ) : (
            <div className="payment-result__complete-note"><ShieldCheck /> Your secure checkout session is complete. You can close this window.</div>
          )}
        </div>

        <div className="payment-result__security"><LockKeyhole /> Encrypted checkout · Payment details protected</div>
      </section>
    </main>
  );
}
